import { InMemoryCache, FieldFunctionOptions, StoreObject, DataProxy } from '@apollo/client';
import possibleTypes from '../../generated/possibleTypes.json';
import arrayTypes from '../../generated/arrayTypes.json';
import { UseUserQuery, UseUserQuery_user as User } from '../../generated/types/UseUserQuery';
import {
  UseCookiesQuery,
  UseCookiesQuery_cookies as Cookies
} from '../../generated/types/UseCookiesQuery';
import {
  ErrorModalQuery,
  ErrorModalQuery_errorModal as ErrorModal
} from '../../generated/types/ErrorModalQuery';
import {
  PaymentErrorQuery,
  PaymentErrorQuery_paymentError as PaymentError
} from '../../generated/types/PaymentErrorQuery';
import {
  NotificationsQuery,
  NotificationsQuery_notifications as Notification
} from '../../generated/types/NotificationsQuery';
import { USER_QUERY, getUserFromCookies } from '../../hooks/useUser';
import { USE_COOKIES_QUERY, getCookies } from '../../hooks/useCookies';
import { ERROR_MODAL_QUERY, ERROR_MODAL_DEFAULTS } from '../../hooks/useErrorModal';
import {
  PAYMENT_ERROR_QUERY,
  PAYMENT_ERROR_DEFAULTS
} from '../../hooks/useCheckout/usePaymentError';
import { NOTIFICATIONS_QUERY } from '../../hooks/useNotifications';

const mergeArrays = <T extends StoreObject>(
  existing: T[],
  incoming: T[],
  { mergeObjects }: FieldFunctionOptions
) => {
  if (existing == null || incoming == null || existing.length !== incoming.length) return incoming;
  return existing.map((item, index) => mergeObjects(item, incoming[index]) as T);
};

export const createCache = () =>
  new InMemoryCache({
    possibleTypes,
    typePolicies: {
      Algorithm: {
        keyFields: ['id', 'attributes', ['algorithm_type']]
      },
      SearchApi: {
        merge: true
      },
      ...Object.keys(arrayTypes).reduce(
        (types, key) => ({
          ...types,
          [key]: {
            fields: arrayTypes[key as keyof typeof arrayTypes].reduce(
              (fields, field) => ({
                ...fields,
                [field]: {
                  merge: mergeArrays
                }
              }),
              {}
            )
          }
        }),
        {}
      )
    }
  });

export type ClientOnlyCacheData = {
  user?: User | null;
  cookies?: Cookies | null;
  errorModal?: ErrorModal | null;
  paymentError?: PaymentError | null;
  notifications?: Notification[];
};

export const initializeCache = (
  cache: DataProxy,
  {
    user = getUserFromCookies(),
    cookies = getCookies(),
    errorModal = ERROR_MODAL_DEFAULTS,
    paymentError = PAYMENT_ERROR_DEFAULTS,
    notifications = []
  }: ClientOnlyCacheData
) => {
  cache.writeQuery<UseUserQuery>({ query: USER_QUERY, data: { user } });
  cache.writeQuery<UseCookiesQuery>({ query: USE_COOKIES_QUERY, data: { cookies } });
  cache.writeQuery<ErrorModalQuery>({ query: ERROR_MODAL_QUERY, data: { errorModal } });
  cache.writeQuery<PaymentErrorQuery>({ query: PAYMENT_ERROR_QUERY, data: { paymentError } });
  cache.writeQuery<NotificationsQuery>({ query: NOTIFICATIONS_QUERY, data: { notifications } });
};
